import type { StorageAdapter } from '../core/adapters';
import type { MarkConfig, StorageData } from '../types';
import {
  captureRawQueryParams,
  normalizeUrlAttribution,
  type AttributionSnapshot,
} from '../attribution';

type AttributionCaptureConfig = Pick<
  MarkConfig,
  | 'capture_query_params'
  | 'capture_all_query_params'
  | 'query_param_denylist'
  | 'max_captured_query_params'
  | 'max_query_param_value_length'
  | 'attribution_param_map'
  | 'debug'
>;

/** Soft-overlay fields: enrich an existing acquisition touch without replacing it. */
const SOFT_ATTRIBUTION_FIELDS = [
  'campaign_id',
  'coupon_code',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'sub_id1',
  'sub_id2',
  'sub_id3',
  'sub_id4',
  'sub_id5',
] as const;

/**
 * Hard acquisition touches replace the stored snapshot (last-touch across channels).
 * Publisher identity and paid/network click ids count; UTMs/coupons alone do not.
 */
export function isHardAttributionTouch(snapshot: AttributionSnapshot): boolean {
  return Boolean(snapshot.publisher_key || snapshot.click_id);
}

/**
 * Replace on publisher_key / click_id; otherwise overlay soft fields onto the prior touch.
 */
export function mergeOrReplaceAttributionSnapshot(
  previous: AttributionSnapshot | undefined,
  next: AttributionSnapshot
): AttributionSnapshot {
  if (!previous || isHardAttributionTouch(next)) {
    return next;
  }

  const merged: AttributionSnapshot = {
    ...previous,
    captured_at: next.captured_at,
    query: {
      ...previous.query,
      ...next.query,
    },
  };

  for (const field of SOFT_ATTRIBUTION_FIELDS) {
    const value = next[field];
    if (value !== undefined) {
      merged[field] = value;
    }
  }

  if (next.conflicts !== undefined) {
    merged.conflicts = next.conflicts;
  }

  return merged;
}

export function captureAttributionFromUrl(
  storage: StorageAdapter,
  config: AttributionCaptureConfig = {}
) {
  if (typeof window === 'undefined') {
    return;
  }

  const updates = extractAttributionFromSearch(window.location.search, config);
  if (Object.keys(updates).length === 0) {
    return;
  }
  const resolved = resolveAttributionStorageUpdate(storage.getAttribution?.(), updates);
  if (Object.keys(resolved).length > 0) {
    storage.update(resolved);
  }
}

/**
 * Extract a URL attribution update from a search string.
 * Returns an empty object when no recognized signal is present.
 * Callers must run this through resolveAttributionStorageUpdate against prior storage.
 */
export function extractAttributionFromSearch(
  search: string,
  config: AttributionCaptureConfig = {}
): Partial<StorageData> {
  const snapshot = normalizeUrlAttribution(search, config);
  if (!snapshot) {
    // Raw-only capture without a recognized semantic signal does not start a new touch.
    // Keep previous attribution intact; optionally refresh raw query bag only when
    // capture_all / extra allowlist yields values AND there is no existing snapshot.
    const raw = captureRawQueryParams(search, config);
    if (Object.keys(raw).length === 0) {
      return {};
    }
    return {
      query_params: raw,
    };
  }

  return snapshotToStorageUpdate(snapshot);
}

export function snapshotToStorageUpdate(snapshot: AttributionSnapshot): Partial<StorageData> {
  return {
    attribution: snapshot,
    // Mirror legacy fields for older readers during rollout
    last_click_id: snapshot.click_id,
    campaign_id: snapshot.campaign_id,
    query_params: snapshot.query,
  };
}

/**
 * Apply a URL-derived storage update against any previously stored snapshot.
 * Hard touches replace; soft touches merge; raw-only never clears an existing snapshot.
 */
export function resolveAttributionStorageUpdate(
  previous: AttributionSnapshot | undefined,
  next: Partial<StorageData>
): Partial<StorageData> {
  if (next.attribution) {
    const merged = mergeOrReplaceAttributionSnapshot(previous, next.attribution);
    return snapshotToStorageUpdate(merged);
  }
  // Raw-only: keep existing attribution snapshot; refresh legacy query_params mirror only
  // when no snapshot exists yet.
  if (previous) {
    return {};
  }
  return next;
}

export function replaceAttributionSnapshot(
  previous: AttributionSnapshot | undefined,
  next: AttributionSnapshot
): AttributionSnapshot {
  return mergeOrReplaceAttributionSnapshot(previous, next);
}
